/**
 * Per-answer feedback (UC-A5).
 *
 * Two design points that are requirements rather than styling.
 *
 * **A negative rating opens a comment box, a positive one does not.** "This was wrong" is only
 * actionable with the *why*, and asking for it at the moment the employee is annoyed is the only
 * moment they will write it. Asking for a reason after a thumbs-up, by contrast, is friction on
 * the path that went well.
 *
 * **The confirmation states what actually happened.** `sent_to_review` comes back from the server
 * rather than being assumed from the rating, so the interface never tells an employee their report
 * went to review when no flag opened. A product that promises a review and does not deliver one
 * teaches people to stop reporting.
 *
 * Feedback attaches to the server's `message_id`, which only exists once the turn is on the audit
 * record — so the controls appear when the answer is recorded, not when it finishes rendering.
 *
 * Visually quiet on purpose: ghost buttons under a hairline rule. Controls that compete with the
 * answer for attention get clicked reflexively, and a reflexive rating is noise in the supervision
 * queue.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, Button, Textarea, submitFeedback } from "@nab/shared";

import { ACTION_BUTTON } from "./actionButton";

export interface FeedbackControlsProps {
  messageId: string;
  employeeId?: string;
}

type State = "idle" | "commenting" | "sending" | "sent" | "error";

/** The row sits under the answer. The rule above it belongs to the action row in `Message`, which
 *  also holds the copy button — drawing another one here put two hairlines a few pixels apart. */
const ROW = "flex flex-wrap items-center gap-2";

export function FeedbackControls({
  messageId,
  employeeId,
}: FeedbackControlsProps): JSX.Element {
  const { t } = useTranslation("app");
  const [state, setState] = useState<State>("idle");
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");
  const [sentToReview, setSentToReview] = useState(false);

  const options = employeeId ? { employeeId } : {};

  async function send(value: "up" | "down", text?: string) {
    setState("sending");
    setRating(value);
    try {
      const result = await submitFeedback(
        { message_id: messageId, rating: value, ...(text ? { comment: text } : {}) },
        options,
      );
      setSentToReview(result.sent_to_review);
      setState("sent");
    } catch (error) {
      // A 404 here means the answer is not in this employee's conversation, which in practice
      // means a stale page. Reported as an error rather than silently swallowed: a button that
      // does nothing visible is worse than one that says it failed.
      setState("error");
      if (!(error instanceof ApiError)) throw error;
    }
  }

  if (state === "sent") {
    return (
      <p className={`${ROW} text-body-sm text-statusSuccess`} role="status">
        {sentToReview ? t("feedback.sentToReview") : t("feedback.thanks")}
      </p>
    );
  }

  if (state === "commenting") {
    return (
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void send("down", comment.trim() || undefined);
        }}
      >
        <Textarea
          id={`comment-${messageId}`}
          label={t("feedback.commentLabel")}
          value={comment}
          maxLength={2000}
          rows={3}
          onChange={(event) => setComment(event.target.value)}
          placeholder={t("feedback.commentPlaceholder")}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" size="sm">
            {t("feedback.send")}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => void send("down")}>
            {t("feedback.skipComment")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className={ROW} role="group" aria-label={t("feedback.groupLabel")}>
      {/* No "Was this helpful?" label: the design's action row is icons, and a thumb pair in this
          position needs no prompt. The accessible names carry it. */}
      <button
        type="button"
        className={ACTION_BUTTON}
        title={t("feedback.helpful")}
        aria-label={t("feedback.helpful")}
        aria-pressed={rating === "up"}
        disabled={state === "sending"}
        onClick={() => void send("up")}
      >
        <ThumbGlyph up />
      </button>
      <button
        type="button"
        className={ACTION_BUTTON}
        title={t("feedback.notHelpful")}
        aria-label={t("feedback.notHelpful")}
        aria-pressed={rating === "down"}
        disabled={state === "sending"}
        onClick={() => setState("commenting")}
      >
        <ThumbGlyph />
      </button>
      {state === "error" ? (
        <span className="text-body-sm text-statusDanger" role="alert">
          {t("feedback.failed")}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A thumb, drawn rather than typed.
 *
 * This was 👍/👎. An emoji is rendered by the operating system, so the same control was a flat
 * outline on one workstation and a full-colour cartoon hand on the next — the one saturated object
 * on an otherwise flat, hairline screen. Drawn here it inherits `currentColor` and the button's
 * disabled state like everything else.
 *
 * The down thumb is a second pair of paths, mirrored on the block axis by hand, rather than a
 * `-scale-y-100` on the up one. Tailwind's transform utilities rely on `--tw-scale-*` defaults
 * that the preflight emits, and the preflight is off in this project (`tailwind.config.mjs`) — the
 * class compiles to a `transform` whose every variable is unset, which computes to `none`.
 */
function ThumbGlyph({ up = false }: { up?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      className="h-4 w-4"
    >
      {up ? (
        <>
          <path d="M7 21V10l4-8h1a2 2 0 0 1 2 2v5h5a2 2 0 0 1 1.95 2.45l-1.6 7A2 2 0 0 1 17.4 21H7z" />
          <path d="M3 10h4v11H3z" />
        </>
      ) : (
        <>
          <path d="M7 3v11l4 8h1a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 1.95-2.45l-1.6-7A2 2 0 0 0 17.4 3H7z" />
          <path d="M3 14h4V3H3z" />
        </>
      )}
    </svg>
  );
}
